/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Annotation,
  Audio,
  BoundingBox,
  Concept,
  ConceptRelation,
  Data,
  EmailAddress,
  Frame,
  FrameInfo,
  HostedURL,
  Image,
  ImageInfo,
  Input,
  Mask,
  Model,
  ModelVersion,
  Point,
  Polygon,
  Region,
  Status,
  Struct,
  Task,
  TaskAIAssistant,
  TaskConcept,
  TaskConceptAutoAnnotationConfig,
  TaskInputSource,
  TaskReview,
  TaskReviewManualStrategyInfo,
  TaskWorker,
  Text,
  TextInfo,
  ThresholdRange,
  Timestamp,
  User,
  UserDetail,
  Video,
  VideoInfo,
  Visibility,
  Worker,
  Workflow,
  WorkflowNode,
  WorkflowVersion,
} from 'api/grpc/resources';
import { AnnotationItem } from 'modules/Annotations/store/helpers';
import { TaskReviewStrategy } from 'modules/Tasks/types';
import { ApiImage } from 'types/swagger/clarifai-api';
import { dataIsConcepts, dataIsRegions, dataIsFrames } from 'utils/typeguards/predictionOutputs';
import { convertJsRegionInfoToGrpcRegionInfo } from 'utils/grpc/regionInfo';

export const convertJsVisibilityToGrpcVisibility = (visibility: CF.API.Users.Visibility): Visibility => {
  const grpcVisibility = new Visibility();

  grpcVisibility.setGettable(visibility.gettable);

  return grpcVisibility;
};

export const convertJsEmailAddressToGrpcEmailAddress = (emailAddress: CF.API.Users.EmailAddressesEntity): EmailAddress => {
  const grpcEmailAddress = new EmailAddress();

  grpcEmailAddress.setEmail(emailAddress.email);
  grpcEmailAddress.setPrimary(emailAddress.primary);
  grpcEmailAddress.setVerified(emailAddress.verified);

  return grpcEmailAddress;
};

export const convertJsUserDetailToGrpcUserDetail = (userDetail: CF.API.Users.UserDetail): UserDetail => {
  const grpcUserDetail = new UserDetail();

  grpcUserDetail.setBillType(userDetail.bill_type || '');
  grpcUserDetail.setCountry(userDetail.country);
  grpcUserDetail.setEmailAddressesList(userDetail.email_addresses?.map(convertJsEmailAddressToGrpcEmailAddress) || []);
  grpcUserDetail.setMetadata(Struct.fromJavaScript(userDetail.metadata || {}));
  grpcUserDetail.setPrimaryEmail(userDetail.primary_email || '');
  grpcUserDetail.setState(userDetail.state || '');

  return grpcUserDetail;
};

export const convertJsUserToGrpcUser = (user: CF.API.Users.User): User => {
  const grpcUser = new User();

  grpcUser.setId(user.id);
  grpcUser.setPrimaryEmail(user.primary_email);
  grpcUser.setFirstName(user.first_name);
  grpcUser.setLastName(user.last_name);
  grpcUser.setCompanyName(user.company_name);
  grpcUser.setEmailAddressesList(user.email_addresses?.map(convertJsEmailAddressToGrpcEmailAddress) || []);
  grpcUser.setTwoFactorAuthEnabled(user.two_factor_auth_enabled || false);
  grpcUser.setTeamsCount(user.teams_count || 0);
  grpcUser.setJobTitle(user.job_title);
  grpcUser.setJobRole(user.job_role || '');
  grpcUser.setIntention(user.intention || '');
  grpcUser.setMetadata(Struct.fromJavaScript(user.metadata || {}));

  if (user.bill_type) {
    grpcUser.setBillType(user.bill_type);
  }

  if (user.visibility) {
    grpcUser.setVisibility(convertJsVisibilityToGrpcVisibility(user.visibility));
  }

  if (user.user_detail) {
    grpcUser.setUserDetail(convertJsUserDetailToGrpcUserDetail(user.user_detail));
  }

  return grpcUser;
};

export const convertJsConceptToGrpcConcept = (partialConcept: CF.API.ConceptPartial): Concept => {
  const concept = new Concept();
  if (partialConcept.id) concept.setId(partialConcept.id);
  if (partialConcept.name) concept.setName(partialConcept.name);

  if (partialConcept.app_id) {
    concept.setAppId(partialConcept.app_id);
  }

  if (partialConcept.value) {
    concept.setValue(partialConcept.value);
  }
  if (partialConcept.user_id) concept.setUserId(partialConcept.user_id);

  return concept;
};

export const convertJsBoundingBoxToGrpcBoundingBox = (bbox: CF.API.BoundingBox): BoundingBox => {
  const boundingBox = new BoundingBox();

  boundingBox.setTopRow(bbox.top_row);
  boundingBox.setRightCol(bbox.right_col);
  boundingBox.setBottomRow(bbox.bottom_row);
  boundingBox.setLeftCol(bbox.left_col);

  return boundingBox;
};

export const convertJsPolygonToGrpcPolygon = ({ points }: CF.API.Polygon): Polygon => {
  const polygon = new Polygon();

  points.forEach(({ col, row }) => {
    const point = new Point();

    point.setRow(row);
    point.setCol(col);
    polygon.addPoints(point);
  });

  return polygon;
};

export const convertJsMasktoGrpcMask = (mask: CF.API.Mask): Mask => {
  const maskObject = new Mask();

  maskObject.setImage(convertJsInputImageToGrpcImage({ image: mask.image } as CF.API.Inputs.ImageInputData));

  return maskObject;
};

export const convertJsAnnotationToGrpcAnnotation = (annotationItem?: AnnotationItem): Annotation => {
  const data = annotationItem?.data;
  const id = annotationItem?.id;
  const input_id = annotationItem?.input_id;
  const worker = annotationItem?.worker;

  const annotation = new Annotation();
  const annotationData = new Data();

  if (data && dataIsConcepts(data) && data.concepts?.length) {
    data.concepts.forEach((concept) => {
      annotationData.addConcepts(convertJsConceptToGrpcConcept(concept));
    });
  } else if (data && dataIsRegions(data) && data.regions?.length) {
    data.regions.forEach(({ data: d, region_info }) => {
      const region = new Region();
      const regionData = new Data();

      d.concepts?.forEach((concept) => {
        regionData.addConcepts(convertJsConceptToGrpcConcept(concept));
      });

      region.setData(regionData);
      region.setRegionInfo(convertJsRegionInfoToGrpcRegionInfo(region_info));
      annotationData.addRegions(region);
    });
  } else if (data && dataIsFrames(data) && data.frames?.length) {
    data.frames.forEach((frameData) => {
      const grpcFrame = convertJsFrameToGrpcFrame(frameData);
      annotationData.addFrames(grpcFrame);
    });
  }

  // Handle metadata if present
  if (data && 'metadata' in data && data.metadata) {
    annotationData.setMetadata(Struct.fromJavaScript(data.metadata));
  }

  if (id) annotation.setId(id);
  if (input_id) annotation.setInputId(input_id);
  annotation.setData(annotationData);

  if (worker) {
    const workerClass = new Worker();

    if (worker.user) {
      const grpcUser = convertJsUserToGrpcUser(worker.user);
      workerClass.setUser(grpcUser);
    }

    if (worker.model) {
      const grpcModel = convertJsModelToGrpcModel(worker.model);
      workerClass.setModel(grpcModel);
    }

    if (worker.workflow) {
      const grpcWorkflow = convertWorkflowToGrpcWorkflow(worker.workflow);
      workerClass.setWorkflow(grpcWorkflow);
    }
    annotation.setWorker(workerClass);
  }

  return annotation;
};

// TODO rename
export const convertJsHostedDataToGrpcHostedUrl = (hostedData: CF.API.Inputs.HostedData): HostedURL => {
  const hostedUrl = new HostedURL();

  hostedUrl.setPrefix(hostedData.prefix || '');
  hostedUrl.setSuffix(hostedData.suffix || '');
  hostedUrl.setCrossorigin(hostedData.crossorigin || '');

  if (hostedData.sizes?.length) {
    hostedUrl.setSizesList(hostedData.sizes);
  }

  return hostedUrl;
};

export const convertJsInputTextToGrpcText = (textInputData: CF.API.Inputs.TextInputData): Text => {
  const { text: textData } = textInputData;
  const textInfo = new TextInfo();
  const text = new Text();

  text.setAllowDuplicateUrl(textData.allow_duplicate_url || false);
  text.setUrl(textData.thumbnail_url || '');

  if (textData.text_info) {
    const { text_info: textInfoData } = textData;

    textInfo.setCharCount(textInfoData.char_count);
    textInfo.setEncoding(textInfoData.encoding);
    text.setTextInfo(textInfo);
  }

  if (textData.hosted) {
    text.setHosted(convertJsHostedDataToGrpcHostedUrl(textData.hosted));
  }

  if ('raw' in textData && textData.raw) {
    text.setRaw(textData.raw);
  } else if ('url' in textData && textData.url) {
    text.setUrl(textData.url);
  }

  return text;
};

export const convertJsInputAudioToGrpcAudio = (audioInputData: CF.API.Inputs.AudioInputData): Audio => {
  const { audio: audioData } = audioInputData;
  const audio = new Audio();

  audio.setAllowDuplicateUrl(audioData.allow_duplicate_url || false);

  if (audioData.hosted) {
    audio.setHosted(convertJsHostedDataToGrpcHostedUrl(audioData.hosted));
  }

  if ('url' in audioData && audioData.url) {
    audio.setUrl(audioData.url);
  } else if ('base64' in audioData && audioData.base64) {
    audio.setBase64(audioData.base64);
  }

  return audio;
};

export const convertJsInputImageToGrpcImage = (imageInputData: CF.API.Inputs.ImageInputData): Image => {
  const { image: imageData } = imageInputData;
  const image = new Image();

  image.setAllowDuplicateUrl(imageData.allow_duplicate_url || false);

  if (imageData.image_info) {
    const { image_info: imageInfoData } = imageData;
    const imageInfo = new ImageInfo();

    imageInfo.setWidth(imageInfoData.width);
    imageInfo.setHeight(imageInfoData.height);
    imageInfo.setFormat(imageInfoData.format);
    imageInfo.setColorMode(imageInfoData.color_mode);
    image.setImageInfo(imageInfo);
  }

  if (imageData.hosted) {
    image.setHosted(convertJsHostedDataToGrpcHostedUrl(imageData.hosted));
  }

  if ('url' in imageData && imageData.url) {
    image.setUrl(imageData.url);
  } else if ('base64' in imageData && imageData.base64) {
    image.setBase64(imageData.base64);
  }

  return image;
};

export const convertJsHostedImageToGrpcImage = (imageData: ApiImage): Image => {
  const image = new Image();

  if (imageData?.hosted) {
    image.setHosted(convertJsHostedDataToGrpcHostedUrl(imageData.hosted));
  }

  if (imageData?.url) {
    image.setUrl(imageData.url);
  } else if (imageData?.base64) {
    image.setBase64(imageData.base64);
  }

  return image;
};

export const convertJsInputVideoToGrpcVideo = (videoInputData: CF.API.Inputs.VideoInputData): Video => {
  const { video: videoData } = videoInputData;
  const video = new Video();

  video.setUrl(videoData.url || '');
  video.setAllowDuplicateUrl(videoData.allow_duplicate_url || false);
  video.setThumbnailUrl(videoData.thumbnail_url || '');

  if (videoData.hosted) {
    video.setHosted(convertJsHostedDataToGrpcHostedUrl(videoData.hosted));
  }

  if (videoData.video_info) {
    const { video_info: videoInfoData } = videoData;
    const videoInfo = new VideoInfo();

    videoInfo.setWidth(videoInfoData.width);
    videoInfo.setHeight(videoInfoData.height);
    videoInfo.setDurationSeconds(videoInfoData.duration_seconds);
    videoInfo.setFps(videoInfoData.fps);
    videoInfo.setFrameCount(videoInfoData.frame_count);
    videoInfo.setVideoFormat(videoInfoData.video_format);
    video.setVideoInfo(videoInfo);
  }

  return video;
};

export const convertJsInputDataToGrpcData = (inputData: CF.API.Inputs.AnyInputData): Data => {
  const data = new Data();

  if ('text' in inputData && inputData.text) {
    data.setText(convertJsInputTextToGrpcText(inputData));
  }

  if ('audio' in inputData && inputData.audio) {
    data.setAudio(convertJsInputAudioToGrpcAudio(inputData));
  }

  if ('image' in inputData && inputData.image) {
    data.setImage(convertJsInputImageToGrpcImage(inputData));
  }

  if ('video' in inputData && inputData.video) {
    data.setVideo(convertJsInputVideoToGrpcVideo(inputData));
  }

  if ('metadata' in inputData && inputData.metadata) {
    data.setMetadata(Struct.fromJavaScript(inputData.metadata));
  }

  return data;
};

export const convertJsStatusToGrpcStatus = (inputStatus: CF.API.Status): Status => {
  const status = new Status();

  status.setCode(inputStatus.code);

  return status;
};

const convertDateStringToGrpcTimestamp = (dateString: string): Timestamp => {
  const date = new Date(dateString);
  const timestamp = new Timestamp();

  timestamp.fromDate(date);

  return timestamp;
};

export const convertJsInputToGrpcInput = (input: CF.API.Inputs.Input): Input => {
  const grpcInput = new Input();

  grpcInput.setData(convertJsInputDataToGrpcData(input.data));
  grpcInput.setStatus(convertJsStatusToGrpcStatus(input.status));
  grpcInput.setId(input.id);
  grpcInput.setCreatedAt(convertDateStringToGrpcTimestamp(input.created_at));
  grpcInput.setModifiedAt(convertDateStringToGrpcTimestamp(input.modified_at));

  return grpcInput;
};

export const convertJsTaskInputSourceToGrpcTaskInputSource = (taskInputSource: CF.API.LabelerTask.TaskInputSource): TaskInputSource => {
  const grpcTaskInputSource = new TaskInputSource();

  if (taskInputSource.id) {
    grpcTaskInputSource.setId(taskInputSource.id);
  }

  grpcTaskInputSource.setType(taskInputSource.type);

  return grpcTaskInputSource;
};

export const convertJsThresholdRangeToGrpc = (thresholdRange: CF.API.LabelerTask.ThresholdRange): ThresholdRange => {
  const grpcThresholdRange = new ThresholdRange();
  grpcThresholdRange.setIsLowerInclusive(thresholdRange.is_lower_inclusive || false);
  grpcThresholdRange.setIsUpperInclusive(thresholdRange.is_upper_inclusive || false);
  grpcThresholdRange.setLower(thresholdRange.lower);
  grpcThresholdRange.setUpper(thresholdRange.upper);

  return grpcThresholdRange;
};

export const convertJsAutoAnnotationToGrpc = (autoAnnotationConfig: CF.API.LabelerTask.TaskAutoAnnotationConfig): TaskConceptAutoAnnotationConfig => {
  const grpc = new TaskConceptAutoAnnotationConfig();
  if (autoAnnotationConfig.annotation_data_types) grpc.setAnnotationDataTypes(autoAnnotationConfig.annotation_data_types);
  if (autoAnnotationConfig.threshold_range) grpc.setThresholdRange(convertJsThresholdRangeToGrpc(autoAnnotationConfig.threshold_range));
  if (autoAnnotationConfig.status_code) grpc.setStatusCode(autoAnnotationConfig.status_code);
  return grpc;
};

export const convertJsTaskConceptToGrpcTaskConcept = (taskConcept: CF.API.LabelerTask.TaskConcept): TaskConcept => {
  const grpcTaskConcept = new TaskConcept();
  grpcTaskConcept.setConcept(convertJsConceptToGrpcConcept(taskConcept.concept));
  if (taskConcept.auto_annotation_config)
    grpcTaskConcept.setAutoAnnotationConfig(
      convertJsAutoAnnotationToGrpc(taskConcept.auto_annotation_config as CF.API.LabelerTask.TaskAutoAnnotationConfig),
    );
  return grpcTaskConcept;
};

export const convertJsManualStrategyToGrpc = (manualStrategy: CF.API.LabelerTask.ManualStrategyInfo): TaskReviewManualStrategyInfo => {
  const grpcManualStrategy = new TaskReviewManualStrategyInfo();

  if (!manualStrategy) return grpcManualStrategy;
  if (manualStrategy.sample_percentage) grpcManualStrategy.setSamplePercentage(manualStrategy.sample_percentage);
  if (manualStrategy.approval_threshold) grpcManualStrategy.setApprovalThreshold(manualStrategy.approval_threshold);
  return grpcManualStrategy;
};

export const convertJsTaskReviewToGrpcTaskReview = (taskReview: CF.API.LabelerTask.TaskReview): TaskReview => {
  const grpcTaskReview = new TaskReview();

  if (taskReview.manual_strategy_info && taskReview.strategy === TaskReviewStrategy.MANUAL) {
    grpcTaskReview.setManualStrategyInfo(convertJsManualStrategyToGrpc(taskReview.manual_strategy_info as CF.API.LabelerTask.ManualStrategyInfo));
  }

  grpcTaskReview.setStrategy(taskReview.strategy || '');
  grpcTaskReview.setUserIdsList(taskReview.user_ids || []);
  grpcTaskReview.setUsersList(taskReview.users?.map(convertJsUserToGrpcUser) || []);

  return grpcTaskReview;
};

export const convertJsAiAssistToGrpcAiAssist = (taskAiAssist: CF.API.LabelerTask.TaskAiAssist): TaskAIAssistant => {
  const grpcTaskAIAssistant = new TaskAIAssistant();

  if (taskAiAssist.workflow_id) {
    grpcTaskAIAssistant.setWorkflowId(taskAiAssist.workflow_id);
  }

  if (taskAiAssist.workflow) {
    grpcTaskAIAssistant.setWorkflow(convertWorkflowToGrpcWorkflow(taskAiAssist.workflow));
  }

  return grpcTaskAIAssistant;
};

export const convertJsVisibilityToGrpc = (visibility: CF.API.Visibility): Visibility => {
  const grpcVisibility = new Visibility();
  grpcVisibility.setGettable(visibility.gettable);
  return grpcVisibility;
};

export const convertJsVersionToGrpc = (version: CF.API.Workflows.WorkflowVersion): WorkflowVersion => {
  const workflowVersion = new WorkflowVersion();
  workflowVersion.setId(version.id);
  return workflowVersion;
};

export const convertJsModelVersionToGrpc = (modelVersion: CF.API.Models.ModelVersion): ModelVersion => {
  const grpcModelVersion = new ModelVersion();
  grpcModelVersion.setId(modelVersion.id);
  return grpcModelVersion;
};

export const convertJsModelToGrpcModel = (model: CF.API.Models.Model): Model => {
  const grpcModel = new Model();
  const grpcModelVersion = new ModelVersion();

  if (model.id) grpcModel.setId(model.id);
  if (model.name) grpcModel.setName(model.name);
  if (model.app_id) grpcModel.setAppId(model.app_id);
  if (model.user_id) grpcModel.setUserId(model.user_id);
  if (model.model_type_id) grpcModel.setModelTypeId(model.model_type_id);
  if (model.toolkits?.length) grpcModel.setToolkitsList(model.toolkits);
  if (model.use_cases?.length) grpcModel.setToolkitsList(model.use_cases);
  if (model.check_consents?.length) grpcModel.setToolkitsList(model.check_consents);
  if (model.model_version.id) grpcModel.setModelVersion(grpcModelVersion.setId(model.model_version.id));

  return grpcModel;
};

export const convertJsNodesToGrpc = (node: CF.API.Workflows.NodesEntity): WorkflowNode => {
  const workflowNode = new WorkflowNode();
  workflowNode.setId(node.id);
  if (node.model) workflowNode.setModel(convertJsModelToGrpcModel(node.model as unknown as CF.API.Models.Model));
  return workflowNode;
};

export const convertToStruct = (obj: Record<string, string | number | boolean | any[] | null>): Struct => {
  const struct = Struct.fromJavaScript(obj);
  return struct;
};

export const convertWorkflowToGrpcWorkflow = (workflow: Partial<CF.API.Workflows.Workflow>): Workflow => {
  const grpcWorkflow = new Workflow();

  if (workflow.id) {
    grpcWorkflow.setId(workflow.id);
  }

  if (workflow.app_id) {
    grpcWorkflow.setAppId(workflow.app_id);
  }

  if (workflow.user_id) {
    grpcWorkflow.setUserId(workflow.user_id);
  }

  if (workflow.description) {
    grpcWorkflow.setDescription(workflow.description);
  }

  if (workflow.notes) {
    grpcWorkflow.setNotes(workflow.notes);
  }

  if (workflow.is_starred !== undefined) {
    grpcWorkflow.setIsStarred(workflow.is_starred);
  }

  if (workflow.star_count !== undefined) {
    grpcWorkflow.setStarCount(workflow.star_count);
  }

  if (workflow.created_at) {
    const timestamp = new Timestamp();
    timestamp.fromDate(new Date(workflow.created_at));
    grpcWorkflow.setCreatedAt(timestamp);
  }

  if (workflow.modified_at) {
    const timestamp = new Timestamp();
    timestamp.fromDate(new Date(workflow.modified_at));
    grpcWorkflow.setModifiedAt(timestamp);
  }

  if (workflow.metadata) {
    grpcWorkflow.setMetadata(Struct.fromJavaScript(workflow.metadata));
  }

  if (workflow.visibility) {
    grpcWorkflow.setVisibility(convertJsVisibilityToGrpc(workflow.visibility));
  }

  if (workflow.image) {
    const image = new Image();
    if (workflow.image.url) {
      image.setUrl(workflow.image.url);
    }
    if (workflow.image.base64) {
      image.setBase64(workflow.image.base64);
    }
    grpcWorkflow.setImage(image);
  }

  if (workflow.check_consents?.length) {
    grpcWorkflow.setCheckConsentsList(workflow.check_consents);
  }

  if (workflow.version) {
    grpcWorkflow.setVersion(convertJsVersionToGrpc(workflow.version));
  }

  if (workflow.nodes?.length) {
    grpcWorkflow.setNodesList(workflow.nodes.map(convertJsNodesToGrpc));
  }

  return grpcWorkflow;
};

export const convertJsModelWorkerToGrpcModelWorker = (modelWorker: CF.API.LabelerTask.TaskModelWorker): Worker => {
  const { model } = modelWorker;

  const grpcModel = convertJsModelToGrpcModel(model as CF.API.Models.Model);
  const grpcWorker = new Worker();

  grpcWorker.setModel(grpcModel);

  return grpcWorker;
};

export const convertJsUserWorkerToGrpcUserWorker = (userWorker: CF.API.LabelerTask.TaskUserWorker): Worker => {
  const { user } = userWorker;

  const grpcUser = convertJsUserToGrpcUser(user);
  const grpcWorker = new Worker();

  grpcWorker.setUser(grpcUser);

  return grpcWorker;
};

const isTaskWorkflowWorker = (
  worker: CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker | CF.API.LabelerTask.TaskUserWorker,
): worker is CF.API.LabelerTask.TaskWorkflowWorker => {
  return 'workflow' in worker;
};

const isTaskModelWorker = (
  worker: CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker | CF.API.LabelerTask.TaskUserWorker,
): worker is CF.API.LabelerTask.TaskModelWorker => {
  return 'model' in worker;
};

const isTaskUserWorker = (
  worker: CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker | CF.API.LabelerTask.TaskUserWorker,
): worker is CF.API.LabelerTask.TaskUserWorker => {
  return 'user' in worker;
};

const isTaskWorker = (
  worker: CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker | CF.API.LabelerTask.TaskUserWorker,
): worker is CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker => {
  return isTaskWorkflowWorker(worker) || isTaskModelWorker(worker) || isTaskUserWorker(worker);
};

export const convertJsWorkersToGrpcWorkers = (
  workers: (CF.API.LabelerTask.TaskWorkflowWorker | CF.API.LabelerTask.TaskModelWorker | CF.API.LabelerTask.TaskUserWorker)[],
): Worker[] => {
  // Each worker is processed exactly once as the types are mutually exclusive:
  // - isTaskWorkflowWorker, isTaskModelWorker, and isTaskUserWorker are disjoint sets
  // - a worker cannot be of multiple types simultaneously
  // This ensures no duplicate workers in the result array
  return workers.reduce<Worker[]>((result, worker) => {
    if (isTaskWorkflowWorker(worker)) {
      const grpcWorker = new Worker();

      if (worker.workflow) {
        // Since TaskWorkflowWorker.workflow is Partial<Workflow>, we need to ensure required fields
        const workflow = {
          id: worker.workflow.id || '',
          app_id: worker.workflow.app_id || '',
          created_at: worker.workflow.created_at || '',
          modified_at: worker.workflow.modified_at || '',
          user_id: worker.workflow.user_id || '',
          ...worker.workflow,
        } as CF.API.Workflows.Workflow;

        grpcWorker.setWorkflow(convertWorkflowToGrpcWorkflow(workflow));
      }

      return [...result, grpcWorker];
    }

    if (isTaskModelWorker(worker)) {
      return [...result, convertJsModelWorkerToGrpcModelWorker(worker)];
    }

    if (isTaskUserWorker(worker)) {
      return [...result, convertJsUserWorkerToGrpcUserWorker(worker)];
    }

    return result;
  }, []);
};

export const convertJsTaskWorkerToGrpcTaskWorker = (taskWorker: CF.API.LabelerTask.TaskWorker): TaskWorker => {
  const grpcTaskWorker = new TaskWorker();

  grpcTaskWorker.setStrategy(taskWorker.strategy || '');
  grpcTaskWorker.setUserIdsList(taskWorker.user_ids || []);
  grpcTaskWorker.setUsersList(taskWorker.users?.map(convertJsUserToGrpcUser) || []);
  grpcTaskWorker.setType(taskWorker.type);

  if (taskWorker.workers && taskWorker.workers.every(isTaskWorker)) {
    grpcTaskWorker.setWorkersList(convertJsWorkersToGrpcWorkers(taskWorker.workers));
  }

  return grpcTaskWorker;
};

export const convertJsTaskToGrpcTask = (task: Partial<CF.API.LabelerTask.Task>): Task => {
  const grpcTask = new Task();

  if (task.id) grpcTask.setId(task.id);
  if (task.concept_ids) grpcTask.setConceptIdsList(task.concept_ids);
  grpcTask.setConceptsList(task.concepts?.map(convertJsTaskConceptToGrpcTaskConcept) || []);

  if (task.input_source) grpcTask.setInputSource(convertJsTaskInputSourceToGrpcTaskInputSource(task.input_source));

  if (task.type) grpcTask.setType(task.type);

  if (task.name) grpcTask.setName(task.name);

  if (task.sample_ms) grpcTask.setSampleMs(task.sample_ms);

  if (task.worker) {
    grpcTask.setWorker(convertJsTaskWorkerToGrpcTaskWorker(task.worker));
  }

  if (task.review) {
    grpcTask.setReview(convertJsTaskReviewToGrpcTaskReview(task.review));
  }

  if (task.ai_assistant) {
    grpcTask.setAiAssistant(convertJsAiAssistToGrpcAiAssist(task.ai_assistant));
  }
  if (task.priority) grpcTask.setPriority(task.priority);
  return grpcTask;
};

export const convertJsConceptRelationToGrpc = (conceptRelation: CF.API.Concepts.ConceptRelation): ConceptRelation => {
  const grpcConceptRelation = new ConceptRelation();

  grpcConceptRelation.setId(conceptRelation.id);
  grpcConceptRelation.setObjectConcept(convertJsConceptToGrpcConcept(conceptRelation.objectConcept));
  grpcConceptRelation.setSubjectConcept(convertJsConceptToGrpcConcept(conceptRelation.subjectConcept));
  grpcConceptRelation.setPredicate(conceptRelation.predicate);

  return grpcConceptRelation;
};

export const convertJsFrameToGrpcFrame = (frameData: CF.API.FramesEntity): Frame => {
  const frame = new Frame();
  const frameInfo = new FrameInfo();
  const data = new Data();

  // Set frame info
  frameInfo.setIndex(frameData.frame_info.index);
  frameInfo.setTime(frameData.frame_info.time);
  frame.setFrameInfo(frameInfo);

  // Set frame id
  frame.setId(frameData.id);

  // Convert frame data based on its type
  if (dataIsRegions(frameData.data) && frameData.data.regions?.length) {
    frameData.data.regions.forEach((regionData) => {
      const region = new Region();
      const regionDataObj = new Data();

      regionData.data.concepts?.forEach((concept) => {
        regionDataObj.addConcepts(convertJsConceptToGrpcConcept(concept));
      });

      region.setData(regionDataObj);
      region.setRegionInfo(convertJsRegionInfoToGrpcRegionInfo(regionData.region_info));
      data.addRegions(region);
    });
  } else if (dataIsConcepts(frameData.data) && frameData.data.concepts?.length) {
    frameData.data.concepts.forEach((concept) => {
      data.addConcepts(convertJsConceptToGrpcConcept(concept));
    });
  }

  // Handle metadata if present
  if (frameData.data && 'metadata' in frameData.data && frameData.data.metadata && Object.keys(frameData.data.metadata).length > 0) {
    data.setMetadata(Struct.fromJavaScript(frameData.data.metadata as Record<string, any>));
  }

  frame.setData(data);
  return frame;
};
